import { ReadonlyVec3, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomTargetObject } from '~/engine/data/common/customs/CustomTargetObject';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { releaseAsymmetricMarginGpu } from '~/engine/util/algorithms/sdf/AsymmetricMarginGL';
import { SDFOperations } from '~/engine/util/algorithms/sdf/SDFOperations';
import { Environment } from '~/engine/data/Environment';
import { getErrorMessage } from '~/utils/errors';
import { waitForUI } from '~/utils/uiUtils';
import { MAX_MARGIN_MM } from './marginRange';
import {
    getPreviewColor,
    PREVIEW_FILL_ALPHA,
} from '~/hooks/structure-set/previewColor';
import { usePreviewBase } from '~/hooks/structure-set/usePreviewBase';
import { useRoiSdfVersions } from '~/hooks/structure-set/useRoiSdfVersions';
import {
    asymmetricPreviewKey,
    IDENTITY_PREVIEW_KEY,
    MarginSource,
    symmetricPreviewKey,
} from './marginPreviewKey';

interface UseMarginPreviewOptions {
    environment: Environment | null;
    inputRoi: RegionOfInterest;
}

export function useMarginPreview({
    environment,
    inputRoi,
}: UseMarginPreviewOptions) {
    const {
        isPreviewing,
        beginPreviewRun,
        endPreviewRun,
        updateView,
        beginOperation,
        setResult,
        consumeResult,
        abortInFlight,
        cleanup,
        heldKey,
    } = usePreviewBase({ environment });

    // Key of the run that last failed, until another run starts. A failure
    // leaves nothing held, which alone reads no differently from "not asked
    // yet", so the panel needs this to offer a retry for these margins.
    const [failedKey, setFailedKey] = useState<string | null>(null);

    // What every preview is computed from, and so what its key names. The
    // content version follows contour edits through useRoiSdfVersions, so
    // an edit to the input structure marks the held preview stale.
    const { roiId } = inputRoi;
    const sdfVersions = useRoiSdfVersions(environment, [roiId]);
    const source = useMemo<MarginSource>(
        () => ({ roiId, sdfVersion: sdfVersions.get(roiId) ?? 0 }),
        [roiId, sdfVersions]
    );

    // The padded "expanded" SDF that the asymmetric passes read from.
    // It depends only on the source texture, so it is built once per
    // source and reused across every preview and apply, then disposed
    // when the source changes, its contours included, or the tool closes.
    // Shared with the apply hook via getExpanded so both read the same
    // texture.
    const expandedRef = useRef<ContourTexture | null>(null);

    useEffect(() => {
        return () => {
            // A run still waiting its turn holds the texture freed below.
            abortInFlight();
            if (expandedRef.current) {
                expandedRef.current.delete();
                expandedRef.current = null;
            }
            // The GPU mipmaps are keyed on the expanded SDF's texture
            // handle, so freeing them here releases that memory and lets
            // the next source rebuild against a fresh expanded.
            releaseAsymmetricMarginGpu();
        };
    }, [inputRoi.texture, source, abortInFlight]);

    const getExpanded = useCallback((): ContourTexture | undefined => {
        const src = inputRoi.texture;
        if (!src) {
            return undefined;
        }
        if (!expandedRef.current) {
            expandedRef.current = SDFOperations.createAsymmetricExpanded(
                src,
                MAX_MARGIN_MM
            );
        }
        return expandedRef.current;
    }, [inputRoi]);

    const handlePreviewSymmetric = useCallback(
        async (marginValue: number) => {
            if (
                !environment?.structureSet ||
                !inputRoi.texture ||
                !environment.image
            ) {
                return;
            }

            if (marginValue === 0) {
                cleanup();
                return;
            }

            const key = symmetricPreviewKey(source, marginValue);
            const signal = beginOperation();
            const runId = beginPreviewRun();
            setFailedKey(null);

            try {
                const marginTexture = await SDFOperations.applySymmetricMargin(
                    inputRoi.texture,
                    marginValue
                );

                if (signal.aborted) {
                    marginTexture.delete();
                    return;
                }

                const previewObj = new CustomTargetObject(
                    environment.image,
                    marginTexture,
                    getPreviewColor(inputRoi.color)
                );
                previewObj.renderOptions = { fillAlpha: PREVIEW_FILL_ALPHA };
                setResult(previewObj, key);
            } catch (e) {
                if (signal.aborted) {
                    // Superseded: cleanup() would abort the replacement
                    // and drop what it publishes, for a failure that no
                    // longer matters.
                    return;
                }
                console.error('Symmetric preview error:', e);
                // Kept on screen, the held preview would pass for this one.
                cleanup();
                setFailedKey(key);
                enqueueSnackbar(`Preview failed: ${getErrorMessage(e)}`, {
                    variant: 'error',
                });
            } finally {
                endPreviewRun(runId);
            }
        },
        [
            environment,
            inputRoi,
            source,
            beginOperation,
            beginPreviewRun,
            endPreviewRun,
            setResult,
            cleanup,
        ]
    );

    const handlePreviewAsymmetric = useCallback(
        async (negLpsMm: vec3, posLpsMm: vec3) => {
            if (
                !environment?.structureSet ||
                !inputRoi.texture ||
                !environment.image
            ) {
                return;
            }

            if (vec3.length(negLpsMm) === 0 && vec3.length(posLpsMm) === 0) {
                cleanup();
                return;
            }

            const key = asymmetricPreviewKey(source, negLpsMm, posLpsMm);
            const signal = beginOperation();
            const runId = beginPreviewRun();
            setFailedKey(null);

            try {
                // Built inside the run so a failed allocation is reported
                // and retried like a failed compute.
                const expanded = getExpanded();
                if (!expanded) {
                    return;
                }
                // TODO: improve these kind of hacky rendering solutions should be removed and
                // heavy computation should be lifted away from blocking the main thread for smooth rendering
                await waitForUI();
                if (signal.aborted) {
                    // Superseded while waiting; the padded SDF may be gone.
                    return;
                }
                const marginTexture = await SDFOperations.applyAsymmetricMargin(
                    expanded,
                    negLpsMm,
                    posLpsMm
                );

                if (signal.aborted) {
                    marginTexture.delete();
                    return;
                }

                const previewObj = new CustomTargetObject(
                    environment.image,
                    marginTexture,
                    getPreviewColor(inputRoi.color)
                );
                previewObj.renderOptions = { fillAlpha: PREVIEW_FILL_ALPHA };
                setResult(previewObj, key);
            } catch (e) {
                if (signal.aborted) {
                    return;
                }
                console.error('Asymmetric preview error:', e);
                cleanup();
                setFailedKey(key);
                enqueueSnackbar(
                    `Margin preview failed: ${getErrorMessage(e)}`,
                    { variant: 'error' }
                );
            } finally {
                endPreviewRun(runId);
            }
        },
        [
            environment,
            inputRoi,
            source,
            getExpanded,
            beginOperation,
            beginPreviewRun,
            endPreviewRun,
            setResult,
            cleanup,
        ]
    );

    const handlePreviewIdentity = useCallback(() => {
        if (
            !environment?.structureSet ||
            !inputRoi.texture ||
            !environment.image
        ) {
            return;
        }
        // All margins are zero: there is nothing to compute, but we still
        // "set the stage" so the other structures dim and the source is
        // highlighted in the preview colour — instead of showing no preview
        // and leaving every structure at full visibility. A clone of the
        // source SDF is used so disposing the preview never frees the
        // source's own texture.
        abortInFlight();
        const identityTexture = inputRoi.texture.clone();
        const previewObj = new CustomTargetObject(
            environment.image,
            identityTexture,
            getPreviewColor(inputRoi.color)
        );
        previewObj.renderOptions = { fillAlpha: PREVIEW_FILL_ALPHA };
        setResult(previewObj, IDENTITY_PREVIEW_KEY);
    }, [environment, inputRoi, abortInFlight, setResult]);

    const consumeSymmetricPreview = useCallback(
        (marginValueMm: number): ContourTexture | undefined =>
            consumeResult(symmetricPreviewKey(source, marginValueMm)),
        [consumeResult, source]
    );

    const consumeAsymmetricPreview = useCallback(
        (
            negLpsMm: ReadonlyVec3,
            posLpsMm: ReadonlyVec3
        ): ContourTexture | undefined =>
            consumeResult(asymmetricPreviewKey(source, negLpsMm, posLpsMm)),
        [consumeResult, source]
    );

    return useMemo(
        () => ({
            isPreviewing,
            handlePreviewSymmetric,
            handlePreviewAsymmetric,
            handlePreviewIdentity,
            consumeSymmetricPreview,
            consumeAsymmetricPreview,
            getExpanded,
            abortInFlight,
            cleanup,
            updateView,
            source,
            heldKey,
            failedKey,
        }),
        [
            isPreviewing,
            handlePreviewSymmetric,
            handlePreviewAsymmetric,
            handlePreviewIdentity,
            consumeSymmetricPreview,
            consumeAsymmetricPreview,
            getExpanded,
            abortInFlight,
            cleanup,
            updateView,
            source,
            heldKey,
            failedKey,
        ]
    );
}
